/**
 * Embed mode: the diagnostic running inside an iframe on a parent page.
 *
 * The same build serves two homes — its own domain, and an Elementor HTML
 * section on the Hard-Wired whitepaper page. Embedded, the app drops its own
 * chrome (the parent page has a header), stops locking itself to the viewport,
 * and hands the parent two things it cannot work out on its own: how tall the
 * content is, and where to scroll when the result appears.
 *
 * The parent side of this contract is `public/embed.js`.
 */

import { logger } from "@/lib/logger";

/** Remembers that this session was opened embedded, so a refresh keeps it. */
export const EMBED_STORAGE_KEY = "hw-embed-v1";

/** The query flag the loader adds to the iframe URL. */
export const EMBED_PARAM = "embed";

/** The query parameter carrying the parent page's own URL. */
export const PARENT_PARAM = "parent";

/** Set on `<html>` while embedded; `globals.css` unlocks the viewport height. */
export const EMBED_CLASS = "hw-embed";

/** What the lead record calls the page when the parent's title is unreadable. */
export const EMBED_PAGE_NAME = "Hard-Wired diagnostic (embedded)";

export const HEIGHT_MESSAGE = "hw:height";
export const SCROLL_TO_MESSAGE = "hw:scroll-to";

/** "Make the frame this tall." Sent whenever the document resizes. */
export interface HeightMessage {
  type: typeof HEIGHT_MESSAGE;
  height: number;
}

/**
 * "Scroll so this part of me is at the top." A cross-origin frame cannot
 * scroll its parent, so it asks instead, in pixels from the top of its own
 * document, with the part's height so the loader can tell whether any of it
 * is on screen. Only sent below the stage width, once when the result appears
 * and once when the report does; the loader ignores it when any part of the
 * target is already in view.
 */
export interface ScrollToMessage {
  type: typeof SCROLL_TO_MESSAGE;
  offset: number;
  height: number;
}

export type EmbedMessage = HeightMessage | ScrollToMessage;

/** How much of a URL is kept, so a crafted `parent` cannot bloat a lead record. */
const MAX_URL_CHARS = 500;

/** How much of a page title is kept, for the same reason. */
const MAX_TITLE_CHARS = 200;

function readQuery(key: string): string | null {
  try {
    return new URLSearchParams(window.location.search).get(key);
  } catch (error) {
    logger.warn("[embed]", "could not read the query string", error);
    return null;
  }
}

function readStoredFlag(): boolean {
  try {
    return window.sessionStorage.getItem(EMBED_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function rememberFlag(): void {
  try {
    window.sessionStorage.setItem(EMBED_STORAGE_KEY, "1");
  } catch (error) {
    logger.warn("[embed]", "could not remember the embed flag", error);
  }
}

/**
 * True when this window is not the top one.
 *
 * Reading `window.top` across origins throws in older browsers; a window that
 * refuses to be compared is, by definition, not the top one.
 */
function isFramed(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

/**
 * Whether the diagnostic is running inside a parent page.
 *
 * The query flag wins and is remembered: a refresh inside the frame arrives
 * without it, and framing alone is not always detectable (a same-origin
 * preview, a browser that hides the ancestor). Being framed is the fallback,
 * so a hand-written iframe without the flag still behaves.
 */
export function isEmbedded(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  if (readQuery(EMBED_PARAM) === "1") {
    rememberFlag();
    return true;
  }
  return readStoredFlag() || isFramed();
}

/** Keep only absolute http(s) URLs: anything else is not a page we came from. */
function asPageUrl(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  if (!trimmed) {
    return null;
  }
  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
      return null;
    }
    return trimmed.slice(0, MAX_URL_CHARS);
  } catch {
    return null;
  }
}

/**
 * The page the visitor is actually looking at: the `parent` parameter the
 * loader passes (already decoded by `URLSearchParams`), the referrer when it
 * does not, and null when neither is usable.
 */
export function getParentPageUrl(): string | null {
  if (typeof window === "undefined") {
    return null;
  }
  const fromParam = asPageUrl(readQuery(PARENT_PARAM));
  if (fromParam) {
    return fromParam;
  }
  return asPageUrl(typeof document === "undefined" ? null : document.referrer);
}

/**
 * The parent page's title, which is only readable when it shares our origin.
 * Cross-origin — the real case — this is null and the caller names the page
 * itself.
 */
export function getParentPageTitle(): string | null {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    if (window.parent === window.self) {
      return null;
    }
    const title = window.parent.document?.title?.trim();
    return title ? title.slice(0, MAX_TITLE_CHARS) : null;
  } catch {
    return null;
  }
}

export function buildHeightMessage(height: number): HeightMessage {
  return {
    type: HEIGHT_MESSAGE,
    height: Number.isFinite(height) ? Math.max(0, Math.ceil(height)) : 0,
  };
}

export function buildScrollToMessage(offset: number, height = 0): ScrollToMessage {
  return {
    type: SCROLL_TO_MESSAGE,
    offset: Number.isFinite(offset) ? Math.max(0, Math.round(offset)) : 0,
    height: Number.isFinite(height) ? Math.max(0, Math.round(height)) : 0,
  };
}

/**
 * Post to whoever framed us.
 *
 * The target origin is `*` because the parent page is not known at build time
 * and the payload is a number: a height and a scroll offset carry nothing that
 * another listener could misuse.
 */
export function postToParent(message: EmbedMessage): void {
  if (typeof window === "undefined" || !window.parent) {
    return;
  }
  try {
    window.parent.postMessage(message, "*");
  } catch (error) {
    logger.warn("[embed]", "could not reach the parent page", error);
  }
}

/** How far an element sits from the top of this document. */
export function documentOffsetTop(element: Element): number {
  const rect = element.getBoundingClientRect();
  return rect.top + (window.scrollY || 0);
}

/** Ask the parent to scroll this element into view. */
export function reportScrollTarget(element: Element): void {
  if (typeof window === "undefined") {
    return;
  }
  const { height } = element.getBoundingClientRect();
  postToParent(buildScrollToMessage(documentOffsetTop(element), height));
}

/** From this frame width up, the embed is a fixed-height two-column stage. */
export const STAGE_MIN_WIDTH = 900;

/**
 * True when the embed is laid out as the fixed 680px stage. The stage never
 * moves or resizes, so a visitor who just answered is already looking at
 * whatever replaces the chat, and the parent page is never asked to scroll.
 */
export function isStageLayout(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia(`(min-width: ${STAGE_MIN_WIDTH}px)`).matches;
}

/** Mark the document as embedded; returns the undo. */
export function markEmbeddedDocument(): () => void {
  if (typeof document === "undefined") {
    return () => {};
  }
  const root = document.documentElement;
  root.classList.add(EMBED_CLASS);
  return () => root.classList.remove(EMBED_CLASS);
}

/**
 * Report the document height to the parent whenever it changes, at most once
 * per frame, and never the same height twice. From 900px the embedded layout
 * is a fixed-height stage, so after the first post nothing changes; below it
 * the stack only grows at the result and at the report. Every resize the
 * observer sees in between (the portrait easing, the chat scrolling in its
 * own box) lands on a height already posted and is dropped.
 *
 * Returns the stop function.
 */
export function startHeightReporting(): () => void {
  if (typeof window === "undefined" || typeof ResizeObserver === "undefined") {
    return () => {};
  }
  let frame = 0;
  let lastHeight = -1;

  const post = () => {
    frame = 0;
    const message = buildHeightMessage(measureAppHeight());
    if (message.height === lastHeight) {
      return;
    }
    lastHeight = message.height;
    logger.debug("[embed]", "height", message.height);
    postToParent(message);
  };

  const schedule = () => {
    if (frame === 0) {
      frame = window.requestAnimationFrame(post);
    }
  };

  const observer = new ResizeObserver(schedule);
  observer.observe(appRoot());
  schedule();

  return () => {
    observer.disconnect();
    if (frame !== 0) {
      window.cancelAnimationFrame(frame);
    }
  };
}

/**
 * The element whose height the frame should match. `#root` holds the whole
 * app; the document itself is a poor source, because inside an iframe its
 * scroll height never drops below the frame's current height, so a frame
 * sized to it can grow but never shrink back.
 */
function appRoot(): HTMLElement {
  return document.getElementById("root") ?? document.body;
}

/** The app's rendered height in whole pixels, rounded up so nothing clips. */
export function measureAppHeight(): number {
  return Math.ceil(appRoot().getBoundingClientRect().height);
}
